import { Component, computed, effect, inject, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { BuildingView, OpportunityBuildingView, SearchBuildingsForOpportunityParameters } from '@api-proxies';
import { IKebabAction } from '@app/components/shared/grids/custom-cell-actions/custom-cell-actions.component';
import { TypeAheadBuildingsComponent } from '@app/components/shared/type-ahead-buildings/type-ahead-buildings.component';
import { GridService } from '@app/services/grid.service';
import { OpportunityBuildingService } from '@http/clients/opportunity-building.service';
import { OpportunityService } from '@http/clients/opportunity.service';
import { ITypeAheadDropdownEntityParameters } from '@interfaces/type-ahead-dropdown-entity-parameters.interface';
import { EntityTypes } from '@models/enums/EntityTypes';
import { OpportunityAction } from '@models/enums/OpportunityAction';
import { AwaiterService } from '@services/awaiter.service';
import { ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ModalEditAddressComponent } from '@shared/modals/modal-edit-address/modal-edit-address.component';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { OPPORTUNITY_BUILDINGS_GRID_COLUMNS } from './opportunities-single-buildings.config';

@Component({
    selector: 'app-opportunities-single-buildings',
    templateUrl: './opportunities-single-buildings.component.html',
    styleUrls: ['./opportunities-single-buildings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent, TypeAheadBuildingsComponent]
})
export class OpportunitiesSingleBuildingsComponent {
    private readonly opportunityBuildingService = inject(OpportunityBuildingService);
    private readonly opportunityService = inject(OpportunityService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly awaiter = inject(AwaiterService);
    private readonly modalService = inject(ModalService);
    private readonly gridService = inject(GridService);

    buildingsDatasource?: IServerSideDatasource;
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    gridColumns: ColDef[] = [...OPPORTUNITY_BUILDINGS_GRID_COLUMNS];

    listEntitiesUpdated = output<void>();

    private readonly previousMainBuildingId = signal<number | null | undefined>(undefined);

    readonly opportunityId = computed(() => this.opportunityService.opportunity()?.id);

    readonly isActionAllowed = computed(() =>
        this.opportunityService.availableActions().includes(OpportunityAction.EditBuilding)
    );

    readonly typeAheadSearchByEntityParameters = computed<ITypeAheadDropdownEntityParameters>(() => {
        const opportunity = this.opportunityService.opportunity();

        return {
            entityKeyName: EntityTypes.Opportunity,
            entity: opportunity?.id
                ? {
                      opportunityId: opportunity.id,
                      organizationId: opportunity.organizationId
                  }
                : undefined
        };
    });

    constructor() {
        this.setupGridColumns();
        this.createDatasource();

        effect(() => {
            const mainBuildingId = this.opportunityService.mainBuildingId();
            const previous = this.previousMainBuildingId();

            if (previous !== undefined && previous !== mainBuildingId) {
                this.refreshGridView();
            }

            this.previousMainBuildingId.set(mainBuildingId);
        });
    }

    getRowIdFn = (params: any) => params.data.buildingId.toString();

    async onSelectedBuilding(building: BuildingView): Promise<void> {
        const opportunityId = this.opportunityId();
        if (!building || !opportunityId) return;
        await this.awaiter.awaitAction('Add building', async () => {
            try {
                const buildingSaved = await this.opportunityBuildingService.save({
                    buildingId: building.id,
                    opportunityId: opportunityId
                } as unknown as OpportunityBuildingView);

                if (buildingSaved) {
                    const gridApi = this.gridService.getGridApi();

                    let updated = false;

                    if (gridApi?.applyServerSideTransaction) {
                        const transactionResult = gridApi.applyServerSideTransaction({
                            add: [buildingSaved]
                        });

                        updated = transactionResult?.status === 'Applied';
                    }

                    if (!updated) {
                        this.refreshGridView();
                    }

                    this.noResults.set(false);
                    this.listEntitiesUpdated.emit();
                }
            } catch (error) {
                console.error(error);
                this.snackbarNotificationService.error('An error occurred while trying to save the building');
            }
        });
    }

    async removeItem(opportunityBuilding: OpportunityBuildingView): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Remove building',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to remove this building from the building list?',
            title: 'Remove building'
        });

        if (!responseOk) return;

        const { id: opportunityBuildingId } = opportunityBuilding;
        if (!opportunityBuildingId) return;

        await this.awaiter.awaitAction(
            'Removing building',
            async () => {
                await this.opportunityBuildingService.remove(opportunityBuildingId);

                const gridApi = this.gridService.getGridApi();
                let removed = false;

                if (gridApi) {
                    const node = gridApi.getRowNode(opportunityBuilding.buildingId.toString());

                    if (node?.data && gridApi.applyServerSideTransaction) {
                        const transactionResult = gridApi.applyServerSideTransaction({
                            remove: [node.data]
                        });
                        removed = transactionResult?.status === 'Applied';
                    }

                    if (removed) {
                        this.noResults.set(gridApi.getDisplayedRowCount() === 0);
                    }
                }

                if (!removed) {
                    this.refreshGridView();
                }

                if (opportunityBuilding.isMain) {
                    this.opportunityService.setMainBuilding(null);
                }

                this.listEntitiesUpdated.emit();
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async refreshGridView(): Promise<void> {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    async setAsMainOffice(opportunityBuilding: OpportunityBuildingView): Promise<void> {
        if (!opportunityBuilding) return;

        const opportunity = this.opportunityService.opportunity();
        if (!opportunity) return;

        if (opportunity.buildingId) {
            const responseOk = await this.modalService.open(ModalConfirmComponent, {
                acceptCaption: 'Confirm',
                cancelCaption: 'Cancel',
                content:
                    "You're about to set a new main building for this opportunity. This will replace your current main building with the selected one. Do you wish to continue?",
                title: 'New Main Building'
            });

            if (!responseOk) return;
        }

        const oldMainBuildingId = opportunity.buildingId;

        await this.awaiter.awaitAction(
            'Setting as Main Building',
            async () => {
                await this.opportunityBuildingService.setAsMain(opportunityBuilding);
                this.setMainBuildingInGrid(opportunityBuilding.buildingId, oldMainBuildingId);
                this.opportunityService.setMainBuilding({
                    ...opportunityBuilding,
                    isMain: true
                });
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async unsetAsMainOffice(opportunityBuilding: OpportunityBuildingView): Promise<void> {
        if (!opportunityBuilding) return;

        await this.awaiter.awaitAction(
            'Unsetting as Main Building',
            async () => {
                await this.opportunityBuildingService.unsetAsMain(opportunityBuilding);
                this.unsetMainBuildingInGrid(opportunityBuilding.buildingId);
                this.opportunityService.setMainBuilding(null);
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async editAddress(opportunityBuilding: OpportunityBuildingView): Promise<void> {
        const updatedOpportunityBuilding = (await this.modalService.open(ModalEditAddressComponent, {
            entityType: EntityTypes.Opportunity,
            opportunityBuilding
        })) as OpportunityBuildingView | undefined;

        if (!updatedOpportunityBuilding) return;

        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        const node = gridApi.getRowNode(updatedOpportunityBuilding.buildingId.toString());
        if (node?.data) {
            Object.assign(node.data, updatedOpportunityBuilding);
            gridApi.redrawRows({ rowNodes: [node] });
        }

        const isMainBuilding =
            updatedOpportunityBuilding.isMain ||
            updatedOpportunityBuilding.buildingId === this.opportunityService.mainBuildingId();

        if (isMainBuilding) {
            this.opportunityService.setMainBuilding(updatedOpportunityBuilding);
        }
    }

    private unsetMainBuildingInGrid(buildingId?: number): void {
        if (!buildingId) return;

        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        const node = gridApi.getRowNode(buildingId.toString());
        if (node?.data) {
            node.data.isMain = false;
            gridApi.redrawRows({ rowNodes: [node] });
        }
    }

    private setMainBuildingInGrid(buildingId?: number, oldMainBuildingId?: number | null): void {
        if (!buildingId) return;

        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        const rowsToUpdate: any[] = [];

        if (oldMainBuildingId && oldMainBuildingId !== buildingId) {
            const oldMainNode = gridApi.getRowNode(oldMainBuildingId.toString());
            if (oldMainNode?.data) {
                oldMainNode.data.isMain = false;
                rowsToUpdate.push(oldMainNode);
            }
        }

        const newMainNode = gridApi.getRowNode(buildingId.toString());
        if (newMainNode?.data) {
            newMainNode.data.isMain = true;
            rowsToUpdate.push(newMainNode);
        }

        if (rowsToUpdate.length > 0) {
            gridApi.redrawRows({ rowNodes: rowsToUpdate });
        }
    }

    private setupGridColumns(): void {
        const actions: IKebabAction[] = [
            {
                label: 'Edit Address Line 2',
                action: (building: OpportunityBuildingView) => {
                    this.editAddress(building);
                },
                show: () => this.isActionAllowed()
            },
            {
                label: 'Set as Main Building',
                action: (building: OpportunityBuildingView) => {
                    this.setAsMainOffice(building);
                },
                show: (building: OpportunityBuildingView) => this.isActionAllowed() && !building.isMain
            },
            {
                label: 'Unset as Main Building',
                action: (building: OpportunityBuildingView) => {
                    this.unsetAsMainOffice(building);
                },
                show: (building: OpportunityBuildingView) => this.isActionAllowed() && !!building.isMain
            },
            {
                label: 'Remove',
                action: (building: OpportunityBuildingView) => this.removeItem(building),
                show: () => this.isActionAllowed()
            }
        ];

        const baseColumns = this.isActionAllowed()
            ? OPPORTUNITY_BUILDINGS_GRID_COLUMNS
            : OPPORTUNITY_BUILDINGS_GRID_COLUMNS.filter((col) => col.colId !== 'actions');

        this.gridColumns = baseColumns.map((col) => {
            if (col.colId === 'actions') {
                return {
                    ...col,
                    cellRendererParams: {
                        actions
                    }
                };
            }
            return col;
        });
    }

    private searchParamsBuilder(gridParams: any): SearchBuildingsForOpportunityParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;

        const opportunityId = this.opportunityId();
        if (!opportunityId) {
            throw new Error('Opportunity is required to search buildings for opportunity.');
        }

        const searchParams = {
            pageNumber,
            pageSize,

            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            opportunityId
        } as SearchBuildingsForOpportunityParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.buildingsDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);
                    this.noResults.set(false);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.opportunityBuildingService.searchForOpportunity(searchParams);

                    if (response?.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                        this.noResults.set(true);
                    }
                } catch (error) {
                    console.error('Error fetching buildings:', error);
                    params.fail();
                    this.noResults.set(true);
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }
}
